'use client';

import { Avatar, AvatarImage } from '@ahafood/ui/components/avatar';
import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { Image } from '@ahafood/ui/components/image';
import { Progress } from '@ahafood/ui/components/progress';
import { Separator } from '@ahafood/ui/components/separator';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { assetPrefix, type Address } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import dayjs from 'dayjs';
import { ArrowRight, Gift, MapPin, Zap } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useParams, useRouter } from 'next/navigation';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import NewOldStoreDeal from './old-deal';
import PromoIcons from './PromoIcons';
import { useGetDeal } from '@/api/use-get-deal';
import {
  useGetDealProgress,
  type Progress as ApiProgress,
} from '@/api/use-get-deal-progress';
import { useGoToOrderWebstore } from '@/api/use-webstore-order';

const LoginDialog = dynamic(
  () =>
    import('@/app/[locale]/(main)/store/components/fb-login').then((m) => ({
      default: m.LoginDialog,
    })),
  { ssr: false }
);
import FreeModeSwiper from '@/app/[locale]/(main)/store/components/items/free-mode-swiper';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { DateFormat, formatUnixTime } from '@/lib/datetime';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile/use-is-mobile';
import { useAiFoodOrderStore, useAuthStore } from '@/store';
import type { MenuItem } from '@/types/item';

export type NewStoreDealProps = {
  cityId: string;
  citySlug: string;
  initialData?: any[]; // Server-provided deal data
};

const formatTime = (timestamp: number) => {
  return formatUnixTime(timestamp, DateFormat.TIME_24);
};

const getTimeRange = (store_open_hours: any) => {
  if (!store_open_hours) return '06:00 - 22:00';

  const today = new Date().getDay();
  const dayKey = today === 0 ? '6' : (today - 1).toString();

  const todayHours = store_open_hours[dayKey];
  if (!todayHours || todayHours.length === 0) return 'Đóng cửa';

  const firstSlot = todayHours[0];
  const lastSlot = todayHours[todayHours.length - 1];

  return `${formatTime(firstSlot.start_time)} - ${formatTime(
    lastSlot.end_time
  )}`;
};

export default function NewStoreDeal({
  cityId,
  citySlug,
  initialData,
}: NewStoreDealProps) {
  // Get locale and city from route params
  const params = useParams();
  const rawCity = Array.isArray(params.city) ? params.city[0] : params.city;
  const router = useRouter();
  const { user, isAuthenticated } = useAuthStore();
  const isMobile = useIsMobile();
  const goToOrderWebstore = useGoToOrderWebstore();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const address = useAiFoodOrderStore.use.address() as Address | null;
  const routes = useCityRoutes();

  // Fetch deals with server-provided initialData
  const { data: dealData } = useGetDeal(
    {
      city_id: cityId,
      skip: 0,
      limit: 40,
    },
    {
      initialData: initialData ? { tags: initialData } : undefined,
    }
  );

  // Fetch deal progress
  const { data: dealProgressData, isLoading } = useGetDealProgress({
    city_id: cityId,
    skip: 0,
    limit: 25,
    ...(address?.lat && address?.lng
      ? {
          lat: address.lat,
          lng: address.lng,
        }
      : {}),
  });

  const deal = dealData?.tags;
  const deal_progress = dealProgressData?.progress;

  useEffect(() => {
    if (isAuthenticated) {
      setShowLoginDialog(false);
    }
  }, [isAuthenticated]);

  const convertToMenuItem = useCallback((dealItem: ApiProgress): MenuItem => {
    return {
      _id: dealItem.item_id,
      name: dealItem.item_name,
      price: dealItem.price,
      discounted_price: dealItem.discounted_price,
      image_thumb_url: dealItem.item_image_url || '',
      is_ai_image: dealItem.is_ai_image ?? false,
    };
  }, []);

  const handleOrderNow = useCallback(
    async (dealItem: ApiProgress) => {
      try {
        trackEvent('click_button', {
          action_time: Math.floor(Date.now() / 1000),
          is_login: isAuthenticated ? 'true' : 'false',
          event_timestamp: dayjs().unix(),
          button_name: 'order_now',
          city_id: cityId,
          button_location: 'flash_deal',
          location_text: address?.address,
          lat: address?.lat,
          lng: address?.lng,
          fb_id: user?.id,
          store_name: dealItem.store_name,
          store_id: dealItem.store_id.toString(),
          store_page_id: dealItem.page_id.toString(),
        });

        const status = dealItem.status;
        if (status === 'OUT_OF_STOCK' || status === 'INACTIVE') {
          router.push(routes.storeDetail(dealItem.store_slug));
          return;
        }

        // Use goToOrderWebstore to open sheet and navigate to store detail
        await goToOrderWebstore(
          dealItem.page_id.toString(),
          dealItem.store_slug,
          '',
          convertToMenuItem(dealItem),
          dealItem.store_id?.toString() || ''
        );
        router.push(routes.storeDetail(dealItem.store_slug));
      } catch (error) {
        console.error('Failed to go to order webstore:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAuthenticated,
      cityId,
      address,
      user?.id,
      router,
      goToOrderWebstore,
      convertToMenuItem,
    ]
  );

  const handleClick = useCallback(
    (dealItem: ApiProgress) => {
      if (!isAuthenticated) {
        setShowLoginDialog(true);
      } else {
        void handleOrderNow(dealItem);
      }
    },
    [isAuthenticated, handleOrderNow]
  );

  const handleCardClick = useCallback(
    (dealItem: ApiProgress) => {
      router.push(routes.storeDetail(dealItem.store_slug));

      // Track card click event
      trackEvent('click_button', {
        action_time: Math.floor(Date.now() / 1000),
        is_login: isAuthenticated ? 'true' : 'false',
        event_timestamp: dayjs().unix(),
        button_name: 'deal_card_click',
        city_id: cityId,
        button_location: 'flash_deal',
        location_text: address?.address,
        lat: address?.lat,
        lng: address?.lng,
        fb_id: user?.id,
        store_name: dealItem.store_name,
        store_id: dealItem.store_id.toString(),
        store_page_id: dealItem.page_id.toString(),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router, isAuthenticated, cityId, address, user?.id]
  );

  const { displayedDeals } = useMemo(() => {
    const deals = deal_progress ?? [];
    // Hide unavailable deals
    const availableDeals = deals.filter(
      (dealItem) => dealItem?.is_available !== false
    );
    const maxDisplayItems = 10;

    // Only slice if needed
    const displayed =
      availableDeals.length > maxDisplayItems
        ? availableDeals.slice(0, maxDisplayItems)
        : availableDeals;

    return {
      displayedDeals: displayed,
    };
  }, [deal_progress]);

  const viewMoreDeals = useCallback(() => {
    router.push(`/${rawCity}/danh-sach-cua-hang-co-hot-deal`);
  }, [rawCity, router]);

  const renderDealBadge = useCallback((dealItem: ApiProgress) => {
    const { discount_config } = dealItem;

    let badgeText = 'Deal';
    if (discount_config.type === 'VALUE') {
      badgeText = `Giảm ${discount_config.value.toLocaleString()}đ`;
    } else if (discount_config.type === 'FLAT') {
      badgeText = `Đồng giá ${discount_config.value.toLocaleString()}đ`;
    } else if (discount_config.type === 'PERCENT') {
      badgeText = `Giảm ${discount_config.value}%`;
    }

    return (
      <div className="absolute top-2 left-2 z-30 pointer-events-none">
        <div className="flex items-center gap-1.5 px-2 py-1 bg-gradient-to-r from-red-50 to-[#FF8C00] text-white text-[10px] md:text-xs font-black uppercase tracking-wider rounded-xl shadow-sm border border-white/30">
          <Zap className="size-2 fill-white" />
          {badgeText}
        </div>
      </div>
    );
  }, []);

  const renderDealCard = useCallback(
    (dealItem: ApiProgress) => {
      const timeRange = getTimeRange(dealItem.store_open_hours);
      const status = dealItem.status;
      const remaining =
        dealItem.max_use === -1 ? -1 : dealItem.max_use - dealItem.usage_count;
      const isUnlimited = dealItem.max_use === -1;

      const progressPercentage = isUnlimited
        ? 50
        : Math.max(
            5,
            ((dealItem.max_use - remaining) / dealItem.max_use) * 100
          );

      return (
        <Card
          // key={`deal-${dealItem.item_id}-${index}`}
          className="group relative h-full flex flex-col bg-white/5 border border-white/10 rounded-[2.2rem] overflow-hidden shadow-md cursor-pointer"
          onClick={() => handleCardClick(dealItem)}
          data-testid="home-flash-deal-item"
          data-index={index}
          data-item-id={dealItem.item_id}
        >
          {/* Image Section */}
          <div className="relative aspect-square overflow-hidden m-1 rounded-[1.8rem]">
            {renderDealBadge(dealItem)}
            {dealItem.total_items && dealItem.total_items > 1 && (
              <div className="absolute top-10 left-2 z-30 px-2 py-1 bg-white/90 text-[#FF6A1E] font-bold text-[10px] md:text-xs rounded-lg shadow-sm border border-white/50 flex items-center">
                <Gift className="size-3 mr-1" />+{dealItem.total_items - 1}
                món
              </div>
            )}

            {/* Top Right Badges */}
            <div className="absolute top-2 right-2 z-30 flex flex-col gap-1.5">
              {dealItem.is_ai_image && (
                <div className="size-6 bg-white/90 rounded-lg shadow-sm border border-white/50 flex items-center justify-center p-1">
                  <Image
                    className="size-4"
                    src={`${assetPrefix}/static/icons/white-ai.svg`}
                    alt="AI"
                    style={{
                      filter:
                        'invert(45%) sepia(87%) saturate(1418%) hue-rotate(338deg) brightness(98%) contrast(101%)',
                    }}
                  />
                </div>
              )}
            </div>

            <Avatar
              className="size-full rounded-none"
              data-testid="home-flash-deal-card-image"
            >
              <AvatarImage
                src={dealItem.item_image_url || dealItem.store_image_url || ''}
                alt={dealItem.item_name || 'Deal image'}
                className="object-cover"
              />
            </Avatar>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent z-50 rounded-[1.8rem]" />
            <div className="absolute bottom-3 inset-x-3 z-[60] flex items-end justify-between">
              <div className="flex flex-col">
                {dealItem.discounted_price ? (
                  <>
                    <span
                      className="text-white text-xl font-black leading-none drop-shadow-lg"
                      data-testid="home-flash-deal-card-discount-price"
                    >
                      {dealItem.discounted_price.toLocaleString()}đ
                    </span>
                    <span
                      className="text-white/60 text-[10px] font-bold line-through drop-shadow-md mt-0.5"
                      data-testid="home-flash-deal-card-origin-price"
                    >
                      {dealItem.price.toLocaleString()}đ
                    </span>
                  </>
                ) : (
                  <span
                    className="text-white text-xl font-black drop-shadow-lg"
                    data-testid="home-flash-deal-card-price"
                  >
                    {dealItem.price.toLocaleString()}đ
                  </span>
                )}
              </div>
              <div className="flex items-end text-white/90 text-[10px] flex-col font-bold">
                <span className="truncate flex items-center">
                  <MapPin className="size-2.5 mr-1 " />
                  {dealItem.distance && dealItem.distance > 0 && (
                    <span data-testid="home-flash-deal-card-distance">
                      {`${(dealItem.distance / 1000).toFixed(1)} km`}
                    </span>
                  )}{' '}
                </span>
                <span>{timeRange}</span>
              </div>
            </div>

            {(status === 'INACTIVE' || status === 'OUT_OF_STOCK') && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-[70]">
                <div className="bg-white/90 px-4 py-2 rounded-2xl shadow-md">
                  <p className="text-neutral-80 font-black text-xs uppercase tracking-widest">
                    Đã hết deal
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Content Section */}
          <div className="px-2 pt-1 pb-2 flex-1 flex flex-col justify-between z-10">
            <div className="mb-1">
              <h2
                className="text-base font-bold text-white line-clamp-1"
                data-testid="home-flash-deal-card-name"
              >
                {dealItem.item_name}
              </h2>
              <div
                className="flex items-center text-xs text-white/70 font-medium truncate"
                data-testid="home-flash-deal-card-store-name"
              >
                {dealItem.store_name}
              </div>
            </div>

            <div className="space-y-1">
              <div className="relative">
                {isUnlimited ? (
                  <>
                    <div className="relative mb-1">
                      <Progress
                        indicatorClassName="bg-gradient-to-r from-red-50 to-primary-50"
                        value={50}
                        className="h-2.5 bg-white/40"
                      />
                      <div
                        className="absolute top-1/2 -translate-y-1/2 z-10"
                        style={{ left: `calc(50% - 10px)` }}
                      >
                        <Zap className="size-5 fill-yellow-20 text-yellow-30" />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="relative mb-1">
                      <Progress
                        value={progressPercentage}
                        className="h-2.5 bg-white/40"
                      />
                      <div
                        className="absolute top-1/2 -translate-y-1/2 z-10"
                        style={{
                          left: `calc(${progressPercentage}% - 10px)`,
                        }}
                      >
                        <Zap className="size-5 fill-yellow-20 text-yellow-30" />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="flex flex-col items-center justify-between text-sm text-white/80">
                <div className="flex flex-col items-left w-full justify-between text-xs">
                  <span className="font-semibold">
                    {isUnlimited
                      ? 'Đang hot 🔥'
                      : `Còn ${remaining}/${dealItem.max_use} đơn`}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <Button
              variant="ghost"
              className={cn(
                'w-full h-11 rounded-3xl font-bold text-sm border-none flex items-center justify-center gap-2 z-10',
                status === 'OUT_OF_STOCK' || status === 'INACTIVE'
                  ? 'bg-white/10 text-white/50'
                  : 'bg-white/20 text-white shadow-sm'
              )}
              onClick={(e) => {
                e.stopPropagation();
                handleClick(dealItem);
              }}
            >
              {status === 'OUT_OF_STOCK' || status === 'INACTIVE' ? (
                <>Xem shop</>
              ) : (
                <>
                  <Zap className="size-4 fill-current" />
                  Đặt ngay
                </>
              )}
            </Button>
          </div>
        </Card>
      );
    },
    [handleCardClick, handleClick, renderDealBadge]
  );

  // Move loading checks here for better control
  if (isLoading || !cityId) {
    return (
      <StoreListWrapper>
        <div className="flex flex-col gap-10 md:gap-20">
          <div className="flex flex-row flex-nowrap gap-4">
            {[...Array(isMobile ? 2 : 3)].map((_, index) => (
              <Skeleton
                key={`skeleton-loading-${index}`}
                className="h-[272px] w-48 flex-none rounded-3xl bg-neutral-1/75 md:h-[320px] md:w-60"
              />
            ))}
          </div>
        </div>
      </StoreListWrapper>
    );
  }

  // If no data at all for both sections, then return null
  const hasSuperDeals = deal && deal.length > 0;
  const hasFlashDeals = displayedDeals && displayedDeals.length > 0;

  if (!hasSuperDeals && !hasFlashDeals) return null;

  return (
    <StoreListWrapper>
      <div className="flex flex-col gap-7 md:gap-20">
        {/* Render Super Deal if available */}
        {hasSuperDeals && (
          <div className="px-4">
            <NewOldStoreDeal
              deals={deal}
              payload={{ city_id: cityId }}
              city_slug={citySlug}
            />
          </div>
        )}

        {/* Flash Deal */}
        {hasFlashDeals && (
          <div
            className="relative flex flex-col gap-4 px-4 py-8 bg-gradient-to-br from-[#FF7F32] via-[#FF6A1E] to-[#FF3A1E] overflow-hidden items-center md:rounded-2xl"
            data-testid="home-flash-deal-section"
          >
            <PromoIcons />

            <div className="relative z-20 flex items-center gap-2 md:gap-3 w-full justify-between">
              <div className="flex flex-col">
                <Image
                  src={'/static/icons/flash-deal.svg'}
                  alt="flash-deal-title"
                  width={180}
                  height={31}
                  className="object-contain object-bottom"
                />
                <p
                  className="text-white/80 text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] mt-1 ml-1"
                  data-testid="home-flash-deal-title"
                >
                  Săn deal hấp dẫn
                </p>
              </div>
              <Button
                size="icon"
                variant="link"
                onClick={() => {
                  viewMoreDeals();
                  trackEvent('click_button', {
                    action_time: Math.floor(Date.now() / 1000),
                    button_name: 'flash_deal_toggle',
                    button_location: 'flash_deal_section',
                  });
                }}
                className="flex items-center size-9 text-xl font-bold text-[#FF6A1E] bg-white/90 rounded-full shadow-md"
              >
                <ArrowRight className="size-5" />
              </Button>
            </div>
            <FreeModeSwiper
              key={`deals-swiper-${displayedDeals
                .map((deal, idx) => `${deal.item_id}-${idx}`)
                .join('-')}`}
              navigation={false}
              className="w-full !overflow-visible md:h-auto z-20"
              spaceBetween={12}
              pagination
              data-testid="home-flash-deal-list"
            >
              {displayedDeals.map((dealItem, index) => (
                <FreeModeSwiper.Slide
                  key={`deal-${dealItem.item_id}-${dealItem.usage_count}-${index}`}
                  style={{
                    width: isMobile ? '190px' : '260px',
                    height: 'auto',
                  }}
                >
                  <div className="relative h-full py-2">
                    {renderDealCard(dealItem)}
                  </div>
                </FreeModeSwiper.Slide>
              ))}

              {/* {hasMoreDeals && (
                <FreeModeSwiper.Slide
                  key="view-more"
                  style={{
                    width: isMobile ? '180px' : '250px',
                    height: 'auto',
                  }}
                >
                  <div className="group cursor-pointer h-full">
                    <Card
                      className="overflow-hidden h-full border-2 border-dashed border-primary-20/60 shadow-sm flex flex-col bg-white cursor-pointer rounded-2xl transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                      onClick={viewMoreDeals}
                    >
                      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center relative z-10">
                        <div className="size-16 md:size-20 rounded-full bg-gradient-to-br from-primary-40 to-primary-60 flex items-center justify-center mb-4 shadow-lg transition-transform duration-300 group-hover:scale-110">
                          <Zap className="size-8 md:size-10 text-white fill-white" />
                        </div>
                        <h3 className="text-lg font-bold text-primary-60 mb-1">
                          Xem thêm deals
                        </h3>
                        <p className="text-xs text-neutral-50 mb-4 font-medium">
                          Còn nhiều deal hot đang chờ bạn
                        </p>
                        <Button
                          className="bg-primary-50 hover:bg-primary-60 text-white font-bold shadow-md hover:shadow-lg transition-all duration-300 rounded-full px-5"
                          onClick={(e) => {
                            e.stopPropagation();
                            viewMoreDeals();
                          }}
                        >
                          Khám phá ngay
                        </Button>
                      </div>
                    </Card>
                  </div>
                </FreeModeSwiper.Slide>
              )} */}
            </FreeModeSwiper>

            {/* Large centered View All button for Flash Deal */}
            <div className="relative z-20">
              <Button
                onClick={() => {
                  viewMoreDeals();
                  trackEvent('click_button', {
                    action_time: Math.floor(Date.now() / 1000),
                    button_name: 'flash_deal_toggle',
                    button_location: 'flash_deal_section',
                  });
                }}
                variant="outline"
                style={{ boxShadow: '0px 4px 4px 1px #0000001F inset' }}
                className="w-full h-12 md:h-14 bg-white/10 text-white font-black text-sm md:text-base rounded-2xl border-white/30 shadow-md flex items-center justify-center gap-2"
              >
                <span>Xem tất cả Flash Deal</span>
                <ArrowRight className="ml-2 size-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        location="flash_deal"
      />
    </StoreListWrapper>
  );
}

export function StoreListWrapper({
  children,
  payload,
}: PropsWithChildren & { payload?: any }) {
  const campaignParam = payload?.campaign_id;

  return (
    <section className="ahf-deal relative overflow-hidden bg-transparent">
      <div
        className={cn(
          'relative z-[1] mx-auto max-w-screen-xl gap-y-6 md:px-0 md:py-8',
          {
            'pb-10 md:pb-16': campaignParam,
          }
        )}
      >
        <div className="w-full flex items-center justify-center px-4 py-6 text-neutral-50 gap-10 bg-transparent">
          <Separator className="h-px w-full flex-1" />
          <span className="font-semibold">Tổng hợp ưu đãi</span>
          <Separator className="h-px w-full flex-1" />
        </div>
        {children}
      </div>
    </section>
  );
}
